if (!customElements.get('media-card-carousel')) {
  const HOVER_PREVIEW_DELAY = 180;
  const RESUME_PREVIEW_DELAY = 300;

  class MediaCardCarousel extends HTMLElement {
    connectedCallback() {
      if (this.isMovingToProductInformation) return;
      if (this.isConnectedOnce) return;
      this.isConnectedOnce = true;

      this.sliderElement = this.querySelector('[data-media-card-slider]');
      this.controlsElement = this.querySelector('[data-media-card-controls]');
      this.indicatorElement = this.querySelector('[data-media-card-indicator]');
      this.prevButton = this.querySelector('[data-media-card-prev]');
      this.nextButton = this.querySelector('[data-media-card-next]');
      this.indicatorCount = 0;
      this.handleClickBound = this.handleClick.bind(this);
      this.handleWindowLoadBound = this.initSwiper.bind(this);
      this.handleModalInteractionBound = this.handleModalInteraction.bind(this);
      this.handleModalChangeBound = this.handleModalChange.bind(this);

      this.prepareCenteredLoop();
      this.addEventListener('click', this.handleClickBound);
      this.syncVideoTriggers();
      this.setupPreviews();
      this.setupModals();

      if (typeof window.Swiper === 'undefined') {
        window.addEventListener('load', this.handleWindowLoadBound, { once: true });
      } else {
        this.initSwiper();
      }

      if (typeof ResizeObserver !== 'undefined' && this.sliderElement) {
        this.resizeObserver = new ResizeObserver(() => this.swiper?.update());
        this.resizeObserver.observe(this.sliderElement);
      }
    }

    disconnectedCallback() {
      // 同页移动不销毁轮播、视频预览和弹窗，真正移除时仍执行清理。
      if (this.isMovingToProductInformation) return;
      this.removeEventListener('click', this.handleClickBound);
      window.removeEventListener('load', this.handleWindowLoadBound);
      this.resizeObserver?.disconnect();
      this.previewObserver?.disconnect();
      this.autoplayObserver?.disconnect();
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.previewCards?.forEach((card) => {
        card.removeEventListener('pointerenter', this.handlePreviewPointerEnterBound);
        card.removeEventListener('pointerleave', this.handlePreviewPointerLeaveBound);
      });
      this.previewVideos?.forEach((video) => {
        video.pause();
        video.removeEventListener('ended', this.previewEndedHandler);
        video.removeEventListener('error', this.previewEndedHandler);
      });
      this.modalObserver?.disconnect();
      this.swiper?.destroy(true, true);
      this.loopCopies?.forEach(copy => copy.remove());
      this.loopCopies = [];
      this.centeredLoopCount = 0;
      this.resetPreviewHover();
      this.cleanupModal(false);
      this.modalElement?.removeEventListener('click', this.handleModalInteractionBound);
      this.modalElement?.removeEventListener('keyup', this.handleModalInteractionBound);
      this.modalElement?.remove();
      this.swiper = null;
      this.modalElement = null;
      this.modalBody = null;
      this.isConnectedOnce = false;
    }

    prepareCenteredLoop() {
      this.centeredLoopCount = 0;
      this.loopCopies = [];
      if (this.dataset.layout !== 'centered' || this.dataset.loop !== 'true') return;
      const wrapper = this.sliderElement?.querySelector('.swiper-wrapper');
      const slides = wrapper ? [...wrapper.children].filter(slide => slide.classList.contains('swiper-slide')) : [];
      if (slides.length < 2) return;
      this.centeredLoopCount = slides.length;
      // 首尾各预置完整副本，滑动期间不移动 DOM，避免两侧出现空白。
      const makeCopies = () => {
        const fragment = document.createDocumentFragment();
        slides.forEach(slide => {
          const copy = slide.cloneNode(true);
          [copy, ...copy.querySelectorAll('[id], [data-shopify-editor-block]')].forEach(node => {
            node.removeAttribute('id');
            node.removeAttribute('data-shopify-editor-block');
          });
          copy.querySelectorAll('img[loading="lazy"]').forEach(img => { img.loading = 'eager'; });
          this.loopCopies.push(copy);
          fragment.appendChild(copy);
        });
        return fragment;
      };
      wrapper.prepend(makeCopies());
      wrapper.append(makeCopies());
    }

    settleCenteredLoop() {
      const count = this.centeredLoopCount;
      const swiper = this.swiper;
      if (count && swiper) {
        const index = swiper.activeIndex;
        if (index < count || index >= count * 2) {
          // 动画结束后瞬间回到中间同一张卡片，左右内容完全一致。
          this.pausePreview();
          this.classList.add('is-resetting-centered-loop');
          swiper.slideTo(count + ((index % count) + count) % count, 0, false);
          // 立即落实缩放状态，防止副本归位时再次执行缩放动画。
          void this.sliderElement.offsetWidth;
          this.classList.remove('is-resetting-centered-loop');
          this.visiblePreviews.clear();
        }
      }
      this.refreshPreviews(true);
    }

    initSwiper() {
      if (this.swiper || !this.sliderElement || typeof window.Swiper === 'undefined') return;

      const centered = this.dataset.layout === 'centered';
      const desktopCards = centered ? 3 : Number.parseFloat(this.dataset.desktopCards) || 3;
      const mobileCards = centered ? 1.5 : Number.parseFloat(this.dataset.mobileCards) || 1;
      const isProductInformation = Boolean(this.closest('.product-information--inner'));
      const slideCount = this.sliderElement.querySelectorAll('.swiper-slide').length;
      const minimumLoopSlides = Math.ceil(Math.max(desktopCards, mobileCards)) + 1;

      this.swiper = new window.Swiper(this.sliderElement, {
        centeredSlides: centered,
        initialSlide: this.centeredLoopCount ? this.centeredLoopCount + 1 : centered && slideCount > 1 ? 1 : 0,
        slidesPerView: mobileCards,
        slidesPerGroup: 1,
        spaceBetween: centered ? 0 : isProductInformation ? 8 : 15,
        // 卡片不足时使用 rewind，避免 Swiper 循环重排造成空白。
        loop: !this.centeredLoopCount && this.dataset.loop === 'true' && slideCount >= minimumLoopSlides,
        rewind: !this.centeredLoopCount && this.dataset.loop === 'true' && slideCount < minimumLoopSlides,
        // 居中布局提前重排两侧卡片，预留数量不能超过循环可用卡片数。
        loopAdditionalSlides: centered ? Math.min(2, Math.max(0, slideCount - minimumLoopSlides)) : 0,
        lazyPreloadPrevNext: centered ? 2 : 0,
        autoplay: this.dataset.autoplay === 'true' ? {
          delay: Number(this.dataset.autoplayDelay) || 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
          stopOnLastSlide: this.dataset.loop !== 'true',
        } : false,
        grabCursor: true,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        keyboard: {
          enabled: true,
          onlyInViewport: true,
        },
        a11y: {
          enabled: true,
        },
        breakpoints: {
          750: {
            slidesPerView: desktopCards,
            spaceBetween: centered ? 0 : isProductInformation ? 8 : 20,
          },
        },
        on: {
          init: (swiper) => this.syncControls(swiper),
          update: (swiper) => this.syncControls(swiper),
          resize: (swiper) => this.syncControls(swiper),
          breakpoint: (swiper) => this.syncControls(swiper),
          slideChange: (swiper) => this.syncControls(swiper),
          slideChangeTransitionStart: () => {
            // 初始化不算切换；首次实际翻页后不再补播首屏队列。
            if (this.swiper) this.hasChangedSlide = true;
            this.resetPreviewHover();
            this.pausePreview();
          },
          slideChangeTransitionEnd: () => this.settleCenteredLoop(),
          lock: (swiper) => this.syncControls(swiper),
          unlock: (swiper) => this.syncControls(swiper),
        },
      });

      this.syncControls(this.swiper);
      this.refreshPreviews();
      this.carouselInView = false;
      if (typeof IntersectionObserver !== 'undefined') {
        this.autoplayObserver = new IntersectionObserver(([entry]) => {
          this.carouselInView = entry.isIntersecting;
          this.syncAutoplay();
        }, { threshold: 0 });
        this.autoplayObserver.observe(this.sliderElement);
      } else {
        this.carouselInView = true;
      }
      this.syncAutoplay();
    }

    syncAutoplay() {
      const autoplay = this.swiper?.autoplay;
      if (!autoplay) return;
      // Swiper 会将 autoplay: false 转成 { enabled: false }，不能只判断对象是否存在。
      if (this.dataset.autoplay !== 'true' || !this.swiper.params.autoplay?.enabled) {
        if (autoplay.running) autoplay.stop();
        return;
      }
      // 离场、后台或弹窗打开时暂停，恢复可见后继续自动切换。
      const shouldPlay = this.carouselInView && !document.hidden && !this.modalElement?.hasAttribute('open');
      if (shouldPlay && !autoplay.running) autoplay.start();
      else if (!shouldPlay && autoplay.running) autoplay.stop();
    }

    async addCardToCart(button) {
      if (button.disabled) return;
      button.disabled = true;
      const status = button.parentElement.querySelector('[role="status"]');
      status.textContent = '';
      try {
        // 复用 Collection tabs 商品卡片的购物车更新及抽屉打开逻辑。
        const cartRenderer = customElements.get('product-card')?.prototype;
        if (!cartRenderer?.renderContents || !cartRenderer?.getSectionsToRender) {
          throw new Error('Cart is not ready. Please try again.');
        }
        const sections = cartRenderer.getSectionsToRender();
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items: [{ id: button.dataset.variantId, quantity: 1 }],
            sections: sections.map(section => section.section),
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Unable to add item to cart.');
        cartRenderer.renderContents(result);
        document.dispatchEvent(new CustomEvent('cart:item-added', {
          detail: { product: result.items ? result.items[0] : result },
        }));
      } catch (error) {
        status.textContent = error.message || 'Unable to add item to cart.';
      } finally {
        button.disabled = false;
      }
    }

    handleClick(event) {
      const addButton = event.target.closest('[data-media-card-add]');
      if (addButton && this.contains(addButton)) {
        this.addCardToCart(addButton);
        return;
      }

      const videoTrigger = event.target.closest('[data-media-card-video-trigger]');
      if (videoTrigger && this.contains(videoTrigger)) {
        this.openVideo(videoTrigger);
        return;
      }

      const indicator = event.target.closest('[data-media-card-indicator-index]');
      if (indicator && this.contains(indicator) && this.swiper) {
        const index = Number.parseInt(indicator.dataset.mediaCardIndicatorIndex, 10);
        if (!Number.isNaN(index)) {
          if (this.centeredLoopCount) this.swiper.slideTo(this.centeredLoopCount + index);
          else if (this.swiper.params.loop) this.swiper.slideToLoop(index);
          else this.swiper.slideTo(index);
        }
        return;
      }

      const prevButton = event.target.closest('[data-media-card-prev]');
      if (prevButton && this.contains(prevButton) && this.swiper) {
        this.swiper.slidePrev();
        return;
      }

      const nextButton = event.target.closest('[data-media-card-next]');
      if (nextButton && this.contains(nextButton) && this.swiper) {
        this.swiper.slideNext();
      }
    }

    setupPreviews() {
      this.activePreview = null;
      this.resetPreviewHover();
      this.hasChangedSlide = false;
      this.selectedPreview = null;
      this.previewVideos = [...this.querySelectorAll('.media-card-carousel__preview')];
      this.previewCards = [];
      this.visiblePreviews = new Set();
      this.completedPreviews = new WeakSet();
      this.previewEndedHandler = (event) => {
        if (event.currentTarget !== this.hoveredPreview || this.hoveredWasActive) {
          this.completedPreviews.add(event.currentTarget);
        }
        event.currentTarget.classList.remove('is-playing');
        if (this.activePreview === event.currentTarget) this.activePreview = null;
        this.playNextPreview();
      };
      this.visibilityHandler = () => {
        if (document.hidden) {
          this.resetPreviewHover();
          this.pausePreview();
          this.swiper?.autoplay?.stop();
        } else {
          if (this.hoveredPreview && !this.activePreview && this.visiblePreviews.has(this.hoveredPreview) && !this.swiper?.animating && !this.modalElement?.hasAttribute('open')) {
            this.startPreview(this.hoveredPreview, false, true);
          } else {
            this.playNextPreview();
          }
          this.syncAutoplay();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
      if (!this.previewVideos.length || typeof IntersectionObserver === 'undefined') return;
      this.previewVideos.forEach((video) => {
        video.muted = true;
        video.loop = false;
        video.addEventListener('ended', this.previewEndedHandler);
        video.addEventListener('error', this.previewEndedHandler);
      });
      if (this.dataset.hoverPreview === 'true') {
        this.handlePreviewPointerEnterBound = this.handlePreviewPointerEnter.bind(this);
        this.handlePreviewPointerLeaveBound = this.handlePreviewPointerLeave.bind(this);
        this.previewCards = [...new Set(this.previewVideos.map(video => video.closest('.swiper-slide')).filter(Boolean))];
        this.previewCards.forEach((card) => {
          card.addEventListener('pointerenter', this.handlePreviewPointerEnterBound);
          card.addEventListener('pointerleave', this.handlePreviewPointerLeaveBound);
        });
      }
      // 观察卡片与视口的交集；每次重新进入可见范围都允许重播。
      this.previewObserver = new IntersectionObserver(() => this.refreshPreviews(), { threshold: [0, 0.1, 0.5, 1] });
      this.previewVideos.forEach((video) => this.previewObserver.observe(video));
    }

    refreshPreviews(preferNew = false) {
      // 保留切换前的可见集合，动画结束后再判断哪些卡片新进入。
      if (this.swiper?.animating) return;
      const bounds = this.sliderElement.getBoundingClientRect();
      const previous = this.visiblePreviews;
      const visible = this.previewVideos.filter((video) => {
        if (this.dataset.layout === 'centered' && !video.closest('.swiper-slide')?.classList.contains('swiper-slide-active')) return false;
        const rect = video.getBoundingClientRect();
        const width = Math.min(rect.right, bounds.right, window.innerWidth) - Math.max(rect.left, bounds.left, 0);
        const height = Math.min(rect.bottom, bounds.bottom, window.innerHeight) - Math.max(rect.top, bounds.top, 0);
        return width > 0 && height > 0;
      }).sort((a, b) => a.getBoundingClientRect().left - b.getBoundingClientRect().left);
      this.visiblePreviews = new Set(visible);
      if (this.hoveredPreview && !this.visiblePreviews.has(this.hoveredPreview)) {
        this.resetPreviewHover();
        this.pausePreview();
      }
      const entering = visible.filter(video => !previous.has(video));
      entering.forEach(video => this.completedPreviews.delete(video));
      if (!visible.length) {
        this.pausePreview();
        return;
      }
      if (this.activePreview && !this.visiblePreviews.has(this.activePreview)) this.pausePreview();
      if (this.hasChangedSlide && (preferNew || !this.visiblePreviews.has(this.selectedPreview))) {
        const backwards = this.swiper?.swipeDirection === 'prev';
        const active = visible.find(video => video.closest('.swiper-slide')?.classList.contains('swiper-slide-active'));
        const preferred = this.dataset.layout === 'centered' ? active :
          (backwards ? entering[0] : entering[entering.length - 1]) ||
          (backwards ? visible[0] : visible[visible.length - 1]);
        if (!this.hoveredPreview) this.pausePreview();
        this.selectedPreview = preferred || null;
        if (preferred) this.completedPreviews.delete(preferred);
      }
      this.playNextPreview();
    }

    pausePreview() {
      this.activePreview?.pause();
      this.activePreview?.classList.remove('is-playing');
      this.activePreview = null;
    }

    resetPreviewHover() {
      clearTimeout(this.hoverEnterTimer);
      clearTimeout(this.hoverLeaveTimer);
      this.hoverEnterTimer = null;
      this.hoverLeaveTimer = null;
      this.pendingHoverPreview = null;
      this.hoveredPreview = null;
      this.hoveredWasActive = false;
      this.interruptedPreview = null;
    }

    handlePreviewPointerEnter(event) {
      if (event.pointerType !== 'mouse' || document.hidden || this.swiper?.animating || this.modalElement?.hasAttribute('open')) return;
      const video = event.currentTarget.querySelector('.media-card-carousel__preview');
      if (!this.visiblePreviews.has(video)) return;

      clearTimeout(this.hoverLeaveTimer);
      this.hoverLeaveTimer = null;
      if (this.hoveredPreview === video || this.pendingHoverPreview === video) return;
      clearTimeout(this.hoverEnterTimer);
      this.pendingHoverPreview = video;
      // 鼠标短暂掠过卡片时保持原视频，停留后才切换。
      this.hoverEnterTimer = setTimeout(() => this.activateHoveredPreview(video), HOVER_PREVIEW_DELAY);
    }

    handlePreviewPointerLeave(event) {
      if (event.pointerType !== 'mouse') return;
      const video = event.currentTarget.querySelector('.media-card-carousel__preview');
      const wasPending = this.pendingHoverPreview === video;
      if (wasPending) {
        clearTimeout(this.hoverEnterTimer);
        this.hoverEnterTimer = null;
        this.pendingHoverPreview = null;
      }
      if (!this.hoveredPreview || (this.hoveredPreview !== video && !wasPending)) return;

      clearTimeout(this.hoverLeaveTimer);
      // 离开卡片后保留短暂缓冲，避免连续划过卡片时反复续播。
      this.hoverLeaveTimer = setTimeout(() => this.finishPreviewHover(), RESUME_PREVIEW_DELAY);
    }

    activateHoveredPreview(video) {
      if (this.pendingHoverPreview !== video) return;
      this.hoverEnterTimer = null;
      this.pendingHoverPreview = null;
      if (!this.isConnected || !this.visiblePreviews.has(video) || document.hidden || this.swiper?.animating || this.modalElement?.hasAttribute('open')) {
        if (this.hoveredPreview) this.hoverLeaveTimer = setTimeout(() => this.finishPreviewHover(), RESUME_PREVIEW_DELAY);
        return;
      }

      if (this.hoveredPreview && this.hoveredWasActive && this.activePreview === this.hoveredPreview) {
        this.interruptedPreview = this.hoveredPreview;
      } else if (!this.hoveredPreview) {
        this.interruptedPreview = this.activePreview === video ? null : this.activePreview;
      }
      this.hoveredWasActive = this.activePreview === video;
      this.hoveredPreview = video;
      if (this.hoveredWasActive) return;

      // 悬停仅临时接管播放，原顺序队列及其播放进度保持不变。
      const resumeOriginal = this.interruptedPreview === video;
      this.pausePreview();
      if (resumeOriginal) {
        this.interruptedPreview = null;
        this.hoveredWasActive = true;
      }
      this.startPreview(video, !resumeOriginal, !resumeOriginal);
    }

    finishPreviewHover() {
      this.hoverLeaveTimer = null;
      const resumeVideo = this.interruptedPreview;
      const keepPlaying = this.hoveredWasActive && this.activePreview === this.hoveredPreview;
      const hoveredVideo = this.hoveredPreview;
      this.resetPreviewHover();
      if (keepPlaying) return;
      if (this.activePreview === hoveredVideo) this.pausePreview();
      if (this.isConnected && resumeVideo && this.visiblePreviews.has(resumeVideo) && !document.hidden && !this.swiper?.animating && !this.modalElement?.hasAttribute('open')) {
        this.startPreview(resumeVideo, false);
      } else {
        this.playNextPreview();
      }
    }

    playNextPreview() {
      if (!this.isConnected || document.hidden || this.swiper?.animating || this.modalElement?.hasAttribute('open') || this.activePreview || this.hoveredPreview) return;
      const candidates = [...this.visiblePreviews].filter(item => this.dataset.layout !== 'centered' || item.closest('.swiper-slide')?.classList.contains('swiper-slide-active'));
      // 翻页后仅播放本次选中的视频，播完或失败都不回头补播其他卡片。
      const video = this.hasChangedSlide
        ? candidates.find(item => item === this.selectedPreview && !this.completedPreviews.has(item))
        : candidates.find(item => !this.completedPreviews.has(item));
      if (!video) return;
      this.startPreview(video);
    }

    startPreview(video, restart = true, isHoverOverride = false) {
      this.activePreview = video;
      if (restart) video.currentTime = 0;
      video.play().then(() => {
        // 异步播放返回时可能已经滑出视口或打开弹窗。
        if (this.activePreview === video) video.classList.add('is-playing');
        else video.pause();
      }).catch(() => {
        if (this.activePreview !== video) return;
        this.activePreview = null;
        if (!isHoverOverride) this.completedPreviews.add(video);
        this.playNextPreview();
      });
    }

    syncVideoTriggers() {
      this.querySelectorAll('[data-media-card]').forEach((card) => {
        const hasVideo = card.dataset.hasVideo === 'true';
        card.querySelectorAll('[data-media-card-video-trigger]').forEach((trigger) => {
          trigger.disabled = !hasVideo;
          trigger.setAttribute('aria-disabled', String(!hasVideo));
        });
      });
    }

    openVideo(trigger) {
      if (trigger.disabled) return;

      const card = trigger.closest('[data-media-card]');
      const template = card?.querySelector('[data-media-card-video-template]');
      if (!template || !this.modalElement || !this.modalBody) return;

      if (typeof this.modalElement.show !== 'function') return;

      this.resetPreviewHover();
      this.pausePreview();
      this.swiper?.autoplay?.stop();
      this.modalBody.replaceChildren(template.content.cloneNode(true));
      this.activeTrigger = trigger;
      this.modalElement.show(trigger);

      window.requestAnimationFrame(() => {
        this.modalElement.querySelector('[role="dialog"]')?.focus();
      });

      const video = this.modalBody.querySelector('video');
      video?.play().catch(() => {});
    }

    setupModals() {
      this.modalElement = document.querySelector(this.dataset.modalId);
      this.modalBody = this.modalElement?.querySelector('[data-media-card-modal-body]');

      if (!this.modalElement) return;

      this.modalElement.addEventListener('click', this.handleModalInteractionBound);
      this.modalElement.addEventListener('keyup', this.handleModalInteractionBound);

      if (typeof MutationObserver !== 'undefined') {
        this.modalObserver = new MutationObserver(this.handleModalChangeBound);
        this.modalObserver.observe(this.modalElement, {
          attributes: true,
          attributeFilter: ['open'],
        });
      }
    }

    handleModalChange() {
      if (!this.modalElement?.hasAttribute('open')) {
        this.cleanupModal(true);
        this.playNextPreview();
        this.syncAutoplay();
      }
    }

    cleanupModal(restoreFocus) {
      this.modalBody?.querySelectorAll('video').forEach((video) => video.pause());
      this.modalBody?.replaceChildren();

      if (restoreFocus) {
        const target = this.activeTrigger || this.modalElement?.openedBy;
        target?.focus();
      }

      this.activeTrigger = null;
    }

    handleModalInteraction(event) {
      if (!event.currentTarget.hasAttribute('open')) {
        this.cleanupModal(true);
      }
    }

    syncControls(swiper = this.swiper) {
      if (!swiper || !this.controlsElement || !this.indicatorElement) return;

      const snapCount = swiper.snapGrid?.length || 0;
      const slideCount = this.centeredLoopCount || swiper.slides?.length || 0;
      const slidesPerView = Number.parseFloat(swiper.params.slidesPerView) || 1;
      const visibleCardCount = Math.max(1, Math.round(slidesPerView));
      const indicatorCount = swiper.params.loop || this.dataset.layout === 'centered' ? slideCount : Math.min(
        snapCount,
        Math.max(slideCount - visibleCardCount + 1, 1),
      );
      const activeIndicatorIndex = this.centeredLoopCount ? swiper.activeIndex % this.centeredLoopCount : Math.min((swiper.params.loop ? swiper.realIndex : swiper.snapIndex) || 0, indicatorCount - 1);
      const isLocked = swiper.isLocked || indicatorCount <= 1;

      this.classList.toggle('is-locked', isLocked);
      this.controlsElement.hidden = isLocked;

      if (isLocked) return;

      this.renderIndicators(indicatorCount, activeIndicatorIndex, swiper.params.loop ? slideCount : snapCount);
      this.updateNavigation(swiper);
    }

    renderIndicators(count, activeIndex, snapCount) {
      if (this.indicatorCount !== count) {
        this.indicatorElement.replaceChildren();

        const fragment = document.createDocumentFragment();
        for (let index = 0; index < count; index += 1) {
          const button = document.createElement('button');
          const targetIndex = index === count - 1 ? snapCount - 1 : index;

          button.type = 'button';
          button.className = 'media-card-carousel__indicator-item';
          button.dataset.mediaCardIndicatorIndex = String(targetIndex);
          button.setAttribute('aria-label', `Go to carousel position ${index + 1}`);
          fragment.appendChild(button);
        }

        this.indicatorElement.appendChild(fragment);
        this.indicatorCount = count;
      }

      this.indicatorElement
        .querySelectorAll('[data-media-card-indicator-index]')
        .forEach((button, index) => {
          const isActive = index === activeIndex;
          const targetIndex = index === count - 1 ? snapCount - 1 : index;

          button.dataset.mediaCardIndicatorIndex = String(targetIndex);
          button.classList.toggle('is-active', isActive);

          if (isActive) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });
    }

    updateNavigation(swiper) {
      this.querySelectorAll('[data-media-card-prev]').forEach((button) => {
        button.disabled = !this.centeredLoopCount && swiper.isBeginning && !swiper.params.loop && !swiper.params.rewind;
        button.setAttribute('aria-disabled', String(button.disabled));
      });

      this.querySelectorAll('[data-media-card-next]').forEach((button) => {
        button.disabled = !this.centeredLoopCount && swiper.isEnd && !swiper.params.loop && !swiper.params.rewind;
        button.setAttribute('aria-disabled', String(button.disabled));
      });
    }
  }
  customElements.define('media-card-carousel', MediaCardCarousel);
}
